import { type DependencyList, useRef } from "react";

import type { Compare } from "~/tools/compare";
import { isFunction } from "~/tools/is-function";
import { isStrictEqual } from "~/tools/is-strict-equal";
import { map } from "~/tools/map";
import { partition } from "~/tools/partition";
import { pipe } from "~/tools/pipe";

/**
 * Converts the tuple of dependencies to tuple of dependency comparators
 * Every element in the tuple can have its own comparator.
 * Fallbacks to `isStrictEqual` when an element's comparator is not provided or `undefined`.
 */
export type CompareDependencyList<TDeps extends DependencyList> = {
	[K in keyof TDeps]?: Compare<TDeps[K]>;
};

/**
 * The hook maintains dependencies referential equality based on the provided compare function.
 * The compare function runs on each render to determine if the dependencies have changed.
 * If the compare function returns `true`, the hook returns previous dependencies, otherwise it returns the incoming dependencies.
 */
export function useComparedDependencies<TDeps extends DependencyList>(deps: TDeps, compare: Compare<TDeps>): TDeps;

/**
 * The hook maintains dependencies referential equality based on the provided comparators for each element in the tuple.
 * The compare functions runs on each render to determine if the dependencies have changed.
 * If a comparator is not provided for an element, it falls back to `isStrictEqual`.
 * If all comparators return `true`, the hook returns previous dependencies, otherwise it returns the incoming dependencies.
 */
export function useComparedDependencies<TDeps extends DependencyList>(
	deps: TDeps,
	comparators: CompareDependencyList<TDeps>,
): TDeps;

export function useComparedDependencies<TDeps extends DependencyList>(
	deps: TDeps,
	compareOrComparators: Compare<TDeps> | CompareDependencyList<TDeps>,
): TDeps {
	// initialize with null, so the comparators do not run on the first render
	const ref = useRef<null | TDeps>(null);

	if (
		// bypass the checks if the deps length changes
		ref.current?.length !== deps.length ||
		(isFunction(compareOrComparators)
			? !compareOrComparators(ref.current, deps)
			: isAnyDependencyChanged(ref.current, deps, compareOrComparators))
	) {
		ref.current = deps;
	}

	return ref.current;
}

function isAnyDependencyChanged<TDeps extends DependencyList>(
	prev: TDeps,
	next: TDeps,
	comparators: CompareDependencyList<TDeps>,
): boolean {
	const [fast, slow] = pipe(
		prev,
		map((dep, index) => ({
			prev: dep,
			next: next[index],
			compare: comparators.at(index) ?? isStrictEqual,
		})),
		// split into fast and slow comparators assuming that strict equality checks (aka Object.is) are faster
		// biome-ignore lint/style/noRestrictedGlobals: Object.is can be assigned by third-party code
		partition(({ compare }) => compare === isStrictEqual || compare === Object.is),
	);

	return [...fast, ...slow].some((entry) => !entry.compare(entry.prev, entry.next));
}
